import React, { useState } from "react";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

// react-facebook-login defaults to Graph API v2.3 - a version Facebook retired
// years ago - and on mobile it swaps the login dialog for a redirect to
// facebook.com/dialog/oauth, which only works when every page URL the button
// sits on is whitelisted as an OAuth redirect URI. Both leave the user with a
// button that does nothing, so every call site goes through this wrapper
// instead of rendering <FacebookLogin/> directly.
// FB.init() is called with "v" + version, so the leading "v" is stripped here
// and either spelling of the env var works.
const GRAPH_VERSION = (process.env.REACT_APP_FB_GRAPH_VERSION || "23.0").replace(
    /^v/i,
    ""
);

const SCOPE = "public_profile,email";

export const facebookStatusMessage = (status) => {
    if (status === "facebookNotLoaded") {
        return "We couldn't load Facebook sign-in. Please turn off any ad or tracker blocker for this site and try again.";
    }
    if (status === "not_authorized") {
        return "You didn't finish giving UserQ access to your Facebook account. Please try again.";
    }
    if (status === "unknown") {
        // Facebook reports "unknown" both when the user closes the dialog and
        // when it refuses to open at all (for example when this domain is not on
        // the app's JavaScript SDK allow list), so don't claim it was cancelled.
        return "Facebook sign-in didn't complete. Please try again.";
    }
    return "We couldn't sign you in with Facebook. Please try again.";
};

// Facebook validates the origin the SDK is loaded from before it will even
// render the consent screen. When the origin is not on the app's allow list it
// shows "there's an issue with its implementation of Facebook Login" inside the
// popup and then reports nothing more useful than status "unknown", so spell
// out what to check rather than leaving someone staring at a dead button.
const warnAboutFacebookSetup = (status) => {
    if (status !== "unknown" && status !== "not_authorized") {
        return;
    }
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    console.warn(
        [
            "Facebook sign-in failed with status \"" + status + "\".",
            "If the popup showed \"there's an issue with its implementation of Facebook Login\",",
            "Facebook rejected the origin " + origin + " rather than anything in this request.",
            "Check in the Facebook app dashboard (app " + process.env.REACT_APP_FB_ID + "):",
            "  - Facebook Login > Settings > Login with the JavaScript SDK is enabled",
            "  - Facebook Login > Settings > Allowed Domains for the JavaScript SDK contains " + origin,
            "  - Settings > Basic > App Domains contains that host",
            "A Live app serving production traffic will not have localhost on that list;",
            "use a separate Development-mode app for local work and set REACT_APP_FB_ID to it."
        ].join("\n")
    );
};

export default function FacebookAuth({ onSuccess, onError, render }) {
    // Facebook only re-asks for a permission that was already declined when the
    // dialog is reopened with auth_type=rerequest.
    const [authType, setAuthType] = useState("");

    const callback = (response) => {
        if (!response || !response.accessToken) {
            warnAboutFacebookSetup(response ? response.status : null);

            onError(facebookStatusMessage(response ? response.status : null));

            return;
        }
        if (!response.email) {
            setAuthType("rerequest");

            onError(
                "Facebook didn't share an email address with us. Please tap 'Sign in with Facebook' again and allow access to your email, or sign up with your email address instead."
            );

            return;
        }
        setAuthType("");

        onSuccess(response);
    };

    return (
        <FacebookLogin
            appId={process.env.REACT_APP_FB_ID}
            version={GRAPH_VERSION}
            scope={SCOPE}
            fields="name,email"
            authType={authType}
            autoLoad={false}
            disableMobileRedirect={true}
            callback={callback}
            onFailure={callback}
            render={(renderProps) =>
                render({
                    ...renderProps,
                    onClick: (e) => {
                        // Until the SDK has loaded the library swallows the click,
                        // which reads as a dead button. Say so instead.
                        if (!renderProps.isSdkLoaded) {
                            if (e && e.preventDefault) {
                                e.preventDefault();
                            }
                            onError(facebookStatusMessage("facebookNotLoaded"));

                            return;
                        }
                        renderProps.onClick(e);
                    },
                })
            }
        />
    );
}
